import React, { useState, useEffect } from 'react';
import { Col, Row, Form, Card } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
//icons
import { FiEdit } from 'react-icons/fi';
import { IoCloseOutline } from 'react-icons/io5';
//helpers
import { verificateNullValue } from 'helpers';
//components
import Button from 'components/backoffice/buttonCustom';
import CustomInput from 'components/backoffice/customInput';
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import SearchClientInput from 'components/backoffice/customInput/SearchClientInput';
import SearchLibraryUsersInput from 'components/backoffice/customInput/SearchLibraryUsersInput';
import SpinnerComponent from 'components/backoffice/SpinnerComponent';
import BudgetModalUserLibrary from 'components/backoffice/modals/BudgetModalUserLibrary';

const ContestForm = ({
  isEditing,
  onSubmitForm,
  onDelete,
  openModalDelete,
  setOpenModalDelete,
  defaultValues,
  loading,
  getDetailContest,
  idContest,
}) => {
  const { t } = useTranslation(['backoffice']);
  const [isOpenBudgetModal, setIsOpenBudgetModal] = useState(false);
  const [libraryUser, setLibraryUser] = useState({});
  const [errorReason, setErrorReason] = useState('');

  const {
    formState: { errors },
    setValue,
    reset,
    control,
    handleSubmit,
    watch,
    getValues
  } = useForm({});

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'user_library',
  });

  const watchedFields = watch('user_library');

  useEffect(() => {
    if (!isEditing)
      reset(defaultValues);
  }, [reset, defaultValues]);


  useEffect(() => {
    if (defaultValues) {
      setValue('client', verificateNullValue(defaultValues?.client))
      setValue('user_special', verificateNullValue(defaultValues?.user_special))
      setValue('discount', defaultValues?.discount)
      setValue('name', defaultValues?.name)
      if (defaultValues.user_library) {
        const customLibraryUsers = defaultValues?.user_library.map((user) => {
          return {
            customId: user.id,
            username: user.username,
            email: user.email,
            library: user.library,
            budget: user.budget
          }
        })
        setValue('user_library', customLibraryUsers)
      }
    }
  }, [defaultValues])


  const onAddLibraryUsers = (libraryUser) => {
    const alreadyExists = fields.some(
      (item) => item.customId === libraryUser.id
    );

    if (!alreadyExists) {
      append({
        ...libraryUser,
        customId: libraryUser.customId || libraryUser.id,
      });
      setErrorReason('');
    } else {
      setErrorReason(`${t('theUser')} "${libraryUser?.username}" ${t('alreadyExists')}`);
    }
  };

  const handleOnDragEnd = (result) => {
    if (!result.destination) return;
    const items = watchedFields;
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setValue('user_library', items);
  }

  const validateShowMessage = !isEditing && fields?.length === 1;

  const openBudgetModal = (id) => {
    const selectedUser = fields?.find(user => user?.customId === id);
    if (selectedUser) {
      setLibraryUser({
        idUser: selectedUser?.customId,
        idLibrary: selectedUser?.library?.id,
        budget: selectedUser?.budget
      });
      setIsOpenBudgetModal(true);
    }
  }

  const clearErrorReason = () => setErrorReason('');

  return (
    <div className="mt-5">
      <Form onSubmit={handleSubmit(onSubmitForm)}>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={6} className="mb-md-2 mb-lg-0">
            <Controller
              name="name"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              defaultValue=''
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="text"
                  id="nombre_concurso"
                  disabled={false}
                  label={t('name')}
                  placeholder={t('name')}
                  onChange={(e) => onChange(e)}
                  classNameLabel="text-light "
                  value={value}
                />
              )}
            />
            {errors.name && <small className="text-danger ">{errors.name.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={6} className="mt-4 mt-lg-0 mb-md-2 mb-lg-0">
            <Controller
              name="client"
              control={control}
              render={({ field: { onChange, value } }) => (
                <SearchClientInput
                  onSelected={(item) => onChange(item)}
                  defaultClient={value}
                />
              )}
            />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col  sm={12} md={12} lg={6} className="mb-md-2 mb-lg-0">
            <Controller
              name="user_special"
              control={control}
              render={({ field: { onChange, value } }) => (
                <SearchLibraryUsersInput
                  label={t('specialUser')}
                  filterBy="search"
                  onSelected={(item) => onChange(item)}
                  defaultClient={value}
                  fields='id,username'
                  profile='library'
                />
              )}
            />
          </Col>
          <Col sm={12} md={12} lg={6} className="mt-4 mt-lg-0 mb-md-2 mb-lg-0">
            <Controller
              name="discount"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
                validate: {
                  positive: (v) => v >= 0 || t('discountPositive'),
                  maxValue: (v) => v <= 100 || t('discountMaxValue'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="number"
                  id="name_discount"
                  disabled={false}
                  label={t('discount')}
                  placeholder={t('discount')}
                  onChange={(e) => {
                    const val = (typeof e === 'number') ? parseFloat(e) : e;
                    if ((val >= 0 && val <= 100) || val === '') {
                      onChange(val);
                    }
                  }}
                  classNameLabel="text-light"
                  min={0}
                  max={100}
                  value={value}
                />
              )}
            />
            {errors.discount && <small className="text-danger ">{errors.discount.message}</small>}
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={12} className="mb-md-2 mb-lg-0">
            <Controller
              name="user_library"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={() => (
                <SearchLibraryUsersInput
                  filterBy="search"
                  onSelected={onAddLibraryUsers}
                  fields='id,email,username'
                  profile='library'
                  onClearError={clearErrorReason}
                />
              )}
            />
            {(errors?.user_library || fields?.length) && <small className="text-danger ">{errors.user_library?.root?.message}</small>}
            {errorReason && <small className="text-danger ">{errorReason}</small>}
          </Col>
          {validateShowMessage && (
            <h4 className="color-orange-primary mt-5 mb-0">{t('notAssignBudget')}</h4>
          )}
          <DragDropContext onDragEnd={handleOnDragEnd}>
            <Droppable droppableId="droppable" direction="vertical">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} className="mt-3 container-list">
                  {fields
                    .filter((user) => user.username || user.fullname || user.email)
                    .map((list, index) => {
                      return (
                        <Draggable key={list.customId} draggableId={list?.customId?.toString()} index={index}>
                          {(provided, snapshot) => (
                            <Card
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={{
                                width: snapshot.isDragging ? '100%' : null,
                                background: snapshot.isDragging ? '#dee2e6' : null,
                                display: snapshot.isDragging ? 'table' : null,
                                ...provided.draggableProps.style,
                              }}
                              className="mt-3"
                            >
                              <Card.Body className="d-flex">
                                <div className="col-6 d-flex">
                                  <div className={`${!list?.library ? 'w-50' : 'w-41'
                                    }`}>
                                    <h5>{list.username || list.fullname}</h5>
                                    <h5 >{list.email}</h5>
                                  </div>
                                  {
                                    isEditing && <FiEdit className={`m-2 ${!list?.library ? 'desactive-icon' : 'cursor-pointer'}`} size={25} onClick={() => {
                                      if (list.library !== null || list.library !== undefined) {
                                        openBudgetModal(list?.customId)
                                      }
                                    }} />
                                  }
                                  {
                                    !list?.library && <h6 className="m-2 color-orange-primary">{t('noAssociatedLibrary')}</h6>
                                  }
                                </div>
                                <div className="col-6 d-flex justify-content-end">
                                  <IoCloseOutline size="30" onClick={() => remove(index)} />
                                </div>
                              </Card.Body>
                            </Card>
                          )}
                        </Draggable>
                      );
                    })}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Row>
      </Form>
      <div className="my-4 d-flex justify-content-end gap-3">
        <Button
          color="primary"
          onClick={handleSubmit(onSubmitForm)}
          loading={loading}
        >
          {t('save')}
          <SpinnerComponent loading={loading} />
        </Button>
        {isEditing && (
          <Button
            color="secondary"
            onClick={() => setOpenModalDelete(true)}
            loading={loading}
          >
            {t('eliminar')}
          </Button>
        )}
      </div>
      <DeleteModalCustom
        openModal={openModalDelete}
        setOpenModal={setOpenModalDelete}
        handleDelete={onDelete}
        deleteTitle={t('deletecontest')}
        beforeTitle={t('edit', { title: defaultValues?.name })}
      />
      <BudgetModalUserLibrary
        show={isOpenBudgetModal}
        setShow={setIsOpenBudgetModal}
        idContest={idContest}
        idLibrary={libraryUser?.idLibrary}
        idUser={libraryUser?.idUser}
        budget={libraryUser?.budget}
        onUpdate={getDetailContest}
        beforeTitle={t('edit', { title: defaultValues?.name })}
      />
    </div>
  );
};

ContestForm.propTypes = {
  onSubmitForm: PropTypes.func.isRequired,
  defaultValues: PropTypes.object,
  isEditing: PropTypes.bool.isRequired,
  openModalDelete: PropTypes.bool,
  setOpenModalDelete: PropTypes.func,
  onDelete: PropTypes.func,
  idContest: PropTypes.number,
  idLibrary: PropTypes.number,
  loading: PropTypes.bool,
  budget: PropTypes.object,
  getDetailContest: PropTypes.func,
};

export default ContestForm;